import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import LongTrendPeriodsClient from "Clients/long-trend-periods-client";
import { Metric, TrendEvent } from "@/types/vesselComparison";
import { ILongTrend } from "@/types/longTrend";
import { ILongTrendPeriod } from "@/types/longTrendPeriod";
import VesselEventsClient from "Clients/vessel-events-client";
import { IConsequenceResult, IConsequences } from "@/types/trendBenefit";
import ConsequencesClient from "@/Scripts/clients/consequences-client";

type VesselComparisonLoading = {
  trends: boolean;
  periods: boolean;
};

type CachedTrends = { ts: number; items: ILongTrend[] };

const TTL_MS_DEFAULT = 10_000;

const longTrendNameMapping = {
  ShipSpeedDev: "Speed Loss"
}

function now() { return Date.now(); }

function toMetric(t: ILongTrend): Metric {
  const mappedName = (longTrendNameMapping as Record<string, string>)[t.name];
  return {
    name: t.name,
    title: mappedName || (t.description || '').split(',')[0] || t.name,
  };
}

@Module({ namespaced: true, name: "VesselComparison" })
class VesselComparison extends VuexModule {
  public loading: VesselComparisonLoading = {
    trends: false,
    periods: false,
  };
  private longTrendByVesselCache: Record<string, CachedTrends> = {};
  private pendingByVessel: Record<string, Promise<ILongTrend[]>> = {};
  public lastPeriodByTrendId: Record<string, { ts: number; period: ILongTrendPeriod }> = {};
  public lastTrendEventsByVesselId: Record<number, { ts: number; event: TrendEvent }> = {};
  public consequencesResultByTrendId: Record<number, { ts: number, results: IConsequenceResult[] }> = {};
  private pendingLastPeriod: Record<string, Promise<ILongTrendPeriod>> = {};

  get trendsFor() {
    return (vesselId: number): ILongTrend[] =>
      this.longTrendByVesselCache[vesselId]?.items ?? [];
  }

  get trendFor() {
    return (vesselId: number, trendName: string): ILongTrend | undefined => {
      const trends = this.trendsFor(vesselId);

      return trends.find(trend => trend.name === trendName);
    }
  }

  get trendIdFor() {
    return (vesselId: number, trendName: string): number | undefined => {
      const trends = this.trendsFor(vesselId);

      return trends.find(trend => trend.name === trendName)?.id;
    }
  }

  get commonMetrics() {
    return (vesselIds: number[]): Metric[] => {
      if (!vesselIds.length) return [];

      const lists = vesselIds.map(id => this.trendsFor(id).map(toMetric));

      if (lists.some(l => l.length === 0)) return [];

      const sets = lists.map(l => new Set(l.map(x => x.name)));
      const commonKeys = [...sets[0]].filter(k => sets.every(s => s.has(k)));

      const map = new Map(lists[0].map(m => [m.name, m]));

      return commonKeys.map(k => map.get(k)!).filter(Boolean);
    };
  }

  get getPeriods() {
    return (payload: { vesselIds: number[]; trendNames: string[] }) => {
      const { vesselIds, trendNames } = payload;

      const result: Record<string, Record<number, ILongTrendPeriod | undefined>> = {};

      for (const name of trendNames) {
        result[name] = {} as Record<number, ILongTrendPeriod | undefined>;
        for (const vId of vesselIds) {
          const tid = this.trendIdFor(vId, name);
          result[name][vId] = tid ? this.lastPeriodFor(tid) : undefined;
        }
      }

      return result;
    }
  }

  get lastPeriodFor() {
    return (trendId: number): ILongTrendPeriod | undefined =>
      this.lastPeriodByTrendId[trendId]?.period;
  }

  get lastTrendEventFor() {
    return (vesselId: number) => {
      return this.lastTrendEventsByVesselId[vesselId]?.event;
    }
  }

  get resultsFor() {
    return (trendId: number): IConsequenceResult[] => {
      return this.consequencesResultByTrendId[trendId]?.results ?? [];
    }
  }

  get loadingState() {
    return this.loading;
  }

  @Mutation
  private SET_VESSEL_TRENDS(payload: { vesselId: string; items: ILongTrend[] }) {
    this.longTrendByVesselCache[payload.vesselId] = { ts: now(), items: payload.items };
  }

  @Mutation
  private SET_LAST_TREND_EVENT(payload: { vesselId: number; event: TrendEvent }) {
    const { vesselId, event } = payload;

    this.lastTrendEventsByVesselId[vesselId] = {
      ts: now(),
      event,
    }
  }

  @Mutation
  private SET_RESULTS_DATA(payload: { trendId: number; results: IConsequenceResult[] }) {
    const { trendId, results } = payload;

    this.consequencesResultByTrendId[trendId] = {
      ts: now(),
      results,
    }
  }

  @Mutation private SET_TRENDS_LOADING(val: boolean) {
    this.loading.trends = val;
  }
  @Mutation private SET_PERIODS_LOADING(val: boolean) {
    this.loading.periods = val;
  }

  @Mutation
  private SET_LAST_PERIOD(payload: { trendId: string; period: ILongTrendPeriod }) {
    this.lastPeriodByTrendId[payload.trendId] = { ts: Date.now(), period: payload.period };
  }

  @Action
  public async ensureTrendsForVessels(payload: { vesselIds: number[]; ttlMs?: number; force?: boolean }) {
    const { vesselIds, ttlMs = TTL_MS_DEFAULT, force = false } = payload;
    const toFetch: number[] = [];

    for (const id of new Set(vesselIds)) {
      const cached = this.longTrendByVesselCache[id];
      const fresh = cached && (now() - cached.ts) < ttlMs;
      if (!fresh || force) toFetch.push(id);
    }

    if (!toFetch.length) return;

    this.context.commit('SET_TRENDS_LOADING', true);

    try {
      const results = await Promise.all(
        toFetch.map(async (id) => {
          if (!this.pendingByVessel[id]) {
            this.pendingByVessel[id] =
              this.context.dispatch('LongTrends/fetchVesselLongTrends', id, { root: true })
                .then(res => res ?? [])
                .catch(() => [] as ILongTrend[])
                .finally(() => { delete this.pendingByVessel[id]; });
          }
          const items = await this.pendingByVessel[id];
          this.context.commit('SET_VESSEL_TRENDS', { vesselId: id, items });
          return items;
        })
      );
      return results;
    } catch {
    } finally {
      this.context.commit('SET_TRENDS_LOADING', false);
    }
  }

  @Action
  public async ensureLastPeriodForTrend(payload: { trendId: string; ttlMs?: number; force?: boolean }) {
    const { trendId, ttlMs = TTL_MS_DEFAULT, force = false } = payload;

    const cached = this.lastPeriodByTrendId[trendId];
    const fresh = cached && (Date.now() - cached.ts) < ttlMs;

    if (fresh && !force) return cached!.period;

    if (!this.pendingLastPeriod[trendId]) {
      // LongTrendPeriodsClient.find is not common Promise, we have to add wrapper to have "finally"
      this.pendingLastPeriod[trendId] = (async () => {
        try {
          const list = await Promise.resolve(
            LongTrendPeriodsClient.find(trendId)
          );
          const period = [...list]
            .reverse()
            .find(p => p?.trendline != null);

          if (period) {
            this.context.commit('SET_LAST_PERIOD', { trendId, period });
          }

          return period;
        } catch {
        } finally {
          delete this.pendingLastPeriod[trendId];
        }
      })();
    }

    const period = await this.pendingLastPeriod[trendId];
    if (period) this.context.commit('SET_LAST_PERIOD', { trendId, period });
    return period;
  }

  @Action
  async ensureLastPeriodsForTrends(payload: { trendIds: string[]; ttlMs?: number; force?: boolean }) {
    const { trendIds, ttlMs, force } = payload;
    const unique = Array.from(new Set(trendIds)).filter(Boolean);
    await Promise.all(
      unique.map(trendId =>
        this.context.dispatch('ensureLastPeriodForTrend', { trendId, ttlMs, force })
      )
    );
  }

  @Action
  async ensureLastPeriodsForSelection(payload: {
    vesselIds: number[];
    trendNames: string[];
    ttlMs?: number;
    force?: boolean;
  }) {
    const { vesselIds, trendNames, ttlMs, force } = payload;
    const trendIds: number[] = [];

    for (const vesselId of vesselIds) {
      for (const trendName of trendNames) {
        const trendId = this.trendIdFor(vesselId, trendName);

        if (trendId) {
          trendIds.push(trendId);
        }
      }
    }

    this.context.commit('SET_PERIODS_LOADING', true);

    await this.context.dispatch('ensureLastPeriodsForTrends', { trendIds, ttlMs, force });

    this.context.commit('SET_PERIODS_LOADING', false);
  }

  @Action
  async ensureLastTrendEvent(payload: { vesselIds: number[]; ttlMs?: number; force?: boolean }) {
    const { vesselIds, ttlMs = TTL_MS_DEFAULT, force = false } = payload;

    for (const vesselId of vesselIds) {
      const cached = this.lastTrendEventsByVesselId[vesselId];
      const fresh = cached && (Date.now() - cached.ts) < ttlMs;

      if (fresh && !force) continue;

      const data = await VesselEventsClient.find(vesselId, ["TrendEvent"]);
      this.context.commit("SET_LAST_TREND_EVENT", { vesselId, event: data[0] });
    }
  }

  @Action
  async ensureBenefitDataForSelection(payload: { vesselIds: number[], trendName: string, ttlMs?: number; force?: boolean }) {
    const { vesselIds, trendName, ttlMs = TTL_MS_DEFAULT, force = false } = payload;
    const trendIds = vesselIds
      .map(vesselId => {
        const trendId = this.context.getters["trendIdFor"](vesselId, trendName);

        if (!trendId) return;

        const cached = this.consequencesResultByTrendId[trendId];
        const fresh = cached && (Date.now() - cached.ts) < ttlMs;

        if (fresh && !force) return;

        return trendId;
      })
      .filter(Boolean) as number[];

    if (!trendIds.length) return;

    const consequences = await ConsequencesClient.findLatest(trendIds) as Record<number, IConsequences[]>;
    Object.entries(consequences).forEach(([trendId, consequence]) => {

      this.context.commit("SET_RESULTS_DATA", { trendId, results: consequence[1].results });
    });
  }
}

export default VesselComparison;